import api from './api';

export type AllowedModel =
  | 'gpt-4o-mini'
  | 'gpt-4o'
  | 'gpt-4.1-mini'
  | 'gpt-4.1-nano'
  | 'gpt-4.1';

export interface MerchantConfigCreateRequest {
  model: AllowedModel;
  llm_api_key: string;
}

export interface MerchantConfigUpdateRequest {
  model?: AllowedModel | null;
  llm_api_key?: string | null;
}

export interface MerchantConfig {
  model: AllowedModel;
  id: string;
  merchant_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Create a new merchant configuration
 * @param config Configuration data
 * @returns Promise with created merchant configuration
 * @throws 409 if merchant configuration already exists
 * @throws 400 if validation fails
 * @throws 401 if unauthorized
 * @throws 500 if server error
 */
export const createMerchantConfig = async (
  config: MerchantConfigCreateRequest,
): Promise<MerchantConfig> => {
  try {
    const response = await api.post<MerchantConfig>(
      '/api/v1/merchant-config/',
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create merchant configuration:', error);
    throw error;
  }
};

/**
 * Get current merchant's configuration
 * @returns Promise with merchant configuration
 * @throws 404 if configuration not found
 * @throws 401 if unauthorized
 * @throws 500 if server error
 */
export const getMerchantConfig = async (): Promise<MerchantConfig> => {
  try {
    const response = await api.get<MerchantConfig>(
      '/api/v1/merchant-config/me',
    );
    return response.data;
  } catch (error) {
    console.error('Failed to fetch merchant configuration:', error);
    throw error;
  }
};

/**
 * Update current merchant's configuration
 * @param config Partial configuration data to update
 * @returns Promise with updated merchant configuration
 * @throws 404 if configuration not found
 * @throws 400 if validation fails
 * @throws 401 if unauthorized
 * @throws 500 if server error
 */
export const updateMerchantConfig = async (
  config: MerchantConfigUpdateRequest,
): Promise<MerchantConfig> => {
  try {
    const response = await api.patch<MerchantConfig>(
      '/api/v1/merchant-config/me',
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update merchant configuration:', error);
    throw error;
  }
};

/**
 * Delete current merchant's configuration
 * @throws 404 if configuration not found
 * @throws 401 if unauthorized
 * @throws 500 if server error
 */
export const deleteMerchantConfig = async (): Promise<void> => {
  try {
    await api.delete('/api/v1/merchant-config/me');
  } catch (error) {
    console.error('Failed to delete merchant configuration:', error);
    throw error;
  }
};
